import type { TrpcRouter } from '@api/client';
import { FEATURE_FLAG_ERROR_MESSAGE } from '@feature-flags';

import { Router } from '@angular/router';

import { Operation, TRPCClientError, TRPCLink } from '@trpc/client';
import { Observer, observable } from '@trpc/server/observable';
import { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc';

import {
  PromotionalDialogConfig,
  PromotionalDialogService,
} from '../shared/ui/services/promotional-dialog/promotional-dialog.service';

/**
 * Context available to error handlers.
 * Add more dependencies as needed (e.g., snackbar service, auth service).
 */
export interface ErrorHandlerContext {
  router: Router;
  promotionalDialogService: PromotionalDialogService;
}

/**
 * Error handler function signature.
 * Returns `true` if the error was handled and should stop propagation to other handlers.
 * Returns `false` to continue to the next handler.
 */
export type TrpcErrorHandler = (
  error: TRPCClientError<TrpcRouter>,
  context: ErrorHandlerContext
) => boolean;

/**
 * Creates a matcher function for TRPC errors based on code and optional message.
 */
export const matchTrpcError = (
  code: TRPC_ERROR_CODE_KEY,
  message?: string
): ((error: TRPCClientError<TrpcRouter>) => boolean) => {
  return error =>
    error.data?.code === code && (message == null || error.message === message);
};

// ============================================================================
// Error Handlers
// ============================================================================

/**
 * Dialog configuration for feature flag access errors.
 */
const FEATURE_FLAG_ERROR_DIALOG: PromotionalDialogConfig = {
  title: 'Feature Not Available',
  description:
    'This feature is currently not enabled for your account. ' +
    'If you believe this is an error, please wait a moment and try again. ' +
    'If the issue persists, contact your administrator for assistance.',
  storageKey: 'feature-flag-error-dialog',
  featureFlag: null,
  isVisible: true,
  severity: 'warning',
  expirationDate: null,
  isTemporary: true,
  hasDownloadButton: false,
};

/**
 * Handles feature flag errors by showing a dialog and redirecting to home page.
 */
export const featureFlagErrorHandler: TrpcErrorHandler = (error, context) => {
  if (matchTrpcError('NOT_FOUND', FEATURE_FLAG_ERROR_MESSAGE)(error)) {
    context.promotionalDialogService.addDialog({
      ...FEATURE_FLAG_ERROR_DIALOG,
    });
    context.router.navigate(['/']);
    return true;
  }
  return false;
};

// Add more handlers here as needed:
// export const maintenanceModeHandler: TrpcErrorHandler = (error, context) => { ... };
// export const rateLimitHandler: TrpcErrorHandler = (error, context) => { ... };

// ============================================================================
// Default Handler Chain
// ============================================================================

/**
 * Default list of error handlers, processed in order.
 * First handler to return `true` stops the chain.
 */
export const DEFAULT_ERROR_HANDLERS: TrpcErrorHandler[] = [
  featureFlagErrorHandler,
  // Add more handlers here in order of priority
];

// ============================================================================
// TRPC Link Factory
// ============================================================================

/**
 * Runs the error through the handler chain.
 * Stops at the first handler that returns true.
 */
const runErrorHandlers = (
  error: TRPCClientError<TrpcRouter>,
  context: ErrorHandlerContext,
  handlers: TrpcErrorHandler[]
): void => {
  for (const handler of handlers) {
    if (handler(error, context)) {
      break;
    }
  }
};

/**
 * Creates an error handler callback for the observable subscriber.
 */
const createErrorCallback = (
  context: ErrorHandlerContext,
  handlers: TrpcErrorHandler[],
  observer: Observer<unknown, unknown>
) => {
  return (err: unknown) => {
    if (err instanceof TRPCClientError) {
      runErrorHandlers(err, context, handlers);
    }
    observer.error(err);
  };
};

/**
 * Creates the observable operator for the TRPC link.
 */
const createLinkOperator = (
  context: ErrorHandlerContext,
  handlers: TrpcErrorHandler[]
) => {
  return ({
    next,
    op,
  }: {
    next: (op: Operation) => ReturnType<typeof observable>;
    op: Operation;
  }) =>
    observable(observer =>
      next(op).subscribe({
        next: (value: unknown) => observer.next(value),
        error: createErrorCallback(context, handlers, observer),
        complete: () => observer.complete(),
      })
    );
};

/**
 * Creates a TRPC link that intercepts errors and runs them through a chain of handlers.
 *
 * @param context - Dependencies available to error handlers
 * @param handlers - Array of error handlers (defaults to DEFAULT_ERROR_HANDLERS)
 */
export const createErrorHandlerLink = (
  context: ErrorHandlerContext,
  handlers: TrpcErrorHandler[] = DEFAULT_ERROR_HANDLERS
): TRPCLink<TrpcRouter> => {
  return () => createLinkOperator(context, handlers);
};
